import {
  Appointment,
  AppointmentCancellationReason,
  AppointmentType,
  CareTeamParticipant,
  CareTeamUpdateParamsParticipant,
  Member,
  Slot,
  User,
} from '@allara-health/source-health-client'
import { RoutePaths } from 'containers/Core/Routes'
import { useAlert } from 'context/AlertContext/AlertContextProvider'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import useAppointmentType from 'hooks/useAppointments/useAppointmentType'
import UseSourceAppointment from 'hooks/useAppointments/useSourceAppointment'
import useSearchParams from 'hooks/useSearchParams'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import { BookingEventSource } from 'utils/amplitude.types'
import { Logger } from 'utils/logger.utils'

export type SchedulingScreen =
  | 'appointmentTypeSelect'
  | 'location'
  | 'book'
  | 'confirm'
  | 'success'

export type AmplitudeBookingMetadata = {
  source: BookingEventSource
  patient_action_id: string | null
  patient_action_definition: string | null
}

interface SchedulingContextInterface {
  dateTime?: Date
  timeZone?: string
  provider?: User
  isReschedule?: boolean
  rebookingFlowData: {
    provider: User
    appointmentType: AppointmentType
    dateTime: Date
    timeZone: string
  } | null
  amplitudeBookingMetadata: AmplitudeBookingMetadata
  isLoadingApptType: boolean
  appointmentType?: AppointmentType
  appointment?: Appointment
  currentScreen: SchedulingScreen
  selectedSlot?: Slot
  participantsChanged: boolean
  setAppointmentTypeId: (appointmentTypeId: string) => void
  setDateTime: (dateTime: Date) => void
  setTimeZone: (timeZone: string) => void
  nextScreen: () => void
  previousScreen: () => void
  bookAppointment: () => Promise<Appointment | null>
  setProvider: (provider?: User) => void
  setCurrentScreen: (screen: SchedulingScreen) => void
  setSelectedSlot: (slot?: Slot) => void
  setRebookingFlowData: (
    rebookingFlowData: {
      provider: User
      appointmentType: AppointmentType
      dateTime: Date
      timeZone: string
    } | null
  ) => void
  cancelAppointment: (
    cancellationReason: AppointmentCancellationReason
  ) => Promise<void>
  rescheduleAppointment: () => Promise<Appointment | null>
}

export const SchedulingContext = createContext<SchedulingContextInterface>(
  {} as SchedulingContextInterface
)

interface SchedulingProviderProps {
  appointmentId?: string
  initialAppointmentTypeId?: string
  initialScreen?: SchedulingScreen
  children: React.ReactNode
}

export const SchedulingProvider: React.FC<SchedulingProviderProps> = ({
  appointmentId,
  initialAppointmentTypeId,
  initialScreen = 'location',
  children,
}) => {
  const logger = new Logger('SchedulingProvider')
  const history = useHistory()
  const { setAlertText } = useAlert()
  const { source } = useSourceContext()
  const searchParams = useSearchParams()
  const [rebookingFlowData, setRebookingFlowData] = useState<{
    provider: User
    appointmentType: AppointmentType
    dateTime: Date
    timeZone: string
  } | null>(null)
  const isReschedule = useMemo(
    () => (appointmentId && !rebookingFlowData ? true : false),
    [appointmentId, rebookingFlowData]
  )
  const [
    amplitudeBookingMetadata,
    setAmplitudeBookingMetadata,
  ] = useState<AmplitudeBookingMetadata>({
    source: BookingEventSource.UNKNOWN,
    patient_action_id: null,
    patient_action_definition: null,
  })
  /*
  this should only ever be called once per booking flow
  note that searchParams are excluded from the dependency array
  because they are not expected to change.
  this exists to track actions outside of the scheduling context that navigate the user into the booking flow
  */
  useEffect(() => {
    const amplitudeBookingMetadata = {
      source:
        (searchParams.get('source') as BookingEventSource) ??
        BookingEventSource.UNKNOWN,
      patient_action_id: searchParams.get('patient_action_id'),
      patient_action_definition: searchParams.get('patient_action_definition'),
    }
    setAmplitudeBookingMetadata(amplitudeBookingMetadata)
  }, [])

  const [appointmentTypeId, setAppointmentTypeId] = useState(
    initialAppointmentTypeId
  )
  const {
    appointment: previousAppointment,
    isLoading: isLoadingAppt,
  } = UseSourceAppointment(appointmentId)
  const {
    appointmentType: fetchedAppointmentType,
    isLoading: isLoadingApptType,
  } = useAppointmentType(appointmentTypeId)
  const [dateTime, _setDateTime] = useState<Date | undefined>(undefined)
  const [timeZone, _setTimeZone] = useState<string | undefined>(undefined)
  const [provider, setProvider] = useState<User | undefined>(undefined)
  const [selectedSlot, setSelectedSlot] = useState<Slot | undefined>(undefined)
  const [appointment, setAppointment] = useState<Appointment | undefined>(
    undefined
  )
  const [currentScreen, setCurrentScreen] = useState<SchedulingScreen>(
    isReschedule ? 'success' : initialScreen
  )
  const [appointmentType, setAppointmentType] = useState<
    AppointmentType | undefined
  >(undefined)
  const [participantsChanged, setParticipantsChanged] = useState<boolean>(false)
  const setDateTime = (dateTime: Date | undefined) => _setDateTime(dateTime)
  const setTimeZone = (timeZone: string | undefined) => _setTimeZone(timeZone)

  const clearAppointmentData = useCallback(() => {
    setAppointment(undefined)
    setDateTime(undefined)
    setProvider(undefined)
  }, [])

  const nextScreen = useCallback(() => {
    switch (currentScreen) {
      case 'appointmentTypeSelect':
        setCurrentScreen('location')
        break
      case 'location':
        setCurrentScreen('book')
        break
      case 'book':
        setCurrentScreen('confirm')
        break
      case 'confirm':
        setCurrentScreen('success')
        break
      case 'success':
        history.push(`${RoutePaths.DASHBOARD}?appointment=${appointment?.id}`)
        break
      default:
        break
    }
  }, [currentScreen, setCurrentScreen, appointment])

  const previousScreen = useCallback(() => {
    switch (currentScreen) {
      case 'book':
        setCurrentScreen('location')
        break
      case 'confirm':
        setCurrentScreen('book')
        break
      case 'location':
        initialScreen === 'appointmentTypeSelect'
          ? setCurrentScreen('appointmentTypeSelect')
          : history.push(RoutePaths.DASHBOARD)
        break
      case 'appointmentTypeSelect':
        history.push(RoutePaths.DASHBOARD)
        break
      case 'success':
      default:
        break
    }
  }, [currentScreen, setCurrentScreen])

  const bookAppointment = useCallback(() => {
    if (!appointmentType || !timeZone || !dateTime || !provider) {
      setAlertText('Please refresh the page and try again', 'An Error Occurred')
      return Promise.resolve(null)
    }

    return source.scheduling.appointments
      .create({
        appointment_type: appointmentType.id,
        time_zone: timeZone,
        start_at: dateTime.toISOString(),
        participants: [{ participant: provider.id }],
      })
      .then((appointment) => {
        setAppointment(appointment)
        setCurrentScreen('success')
        return appointment
      })
  }, [source, appointmentType, timeZone, dateTime, provider])

  const rescheduleAppointment = useCallback(() => {
    if (
      !appointmentType ||
      !appointment ||
      !timeZone ||
      !dateTime ||
      !provider
    ) {
      setAlertText('Please refresh the page and try again', 'An Error Occurred')
      return Promise.resolve(null)
    }

    // TODO: confirm its not identical with existing appointment?

    // Confirm the appointment types match
    const scheduledAppointmentTypeId =
      typeof appointment.appointment_type === 'string'
        ? appointment.appointment_type
        : appointment.appointment_type?.id

    if (scheduledAppointmentTypeId !== appointmentType.id) {
      setAlertText(
        'You cannot update the appointment type for an appointment. Please contact concierge for assistance',
        'Hold On'
      )
      return Promise.resolve(null)
    }

    // If new participant remove video call from appointment so a new one can be created
    // let video_call = appointment.video_call
    const currentParticipant = appointment.participants[0].participant
    const currentProviderId =
      typeof currentParticipant === 'string'
        ? currentParticipant
        : currentParticipant.id

    if (provider.id !== currentProviderId) {
      // video_call = null
      setParticipantsChanged(true)
    }

    return source.scheduling.appointments
      .update(appointment.id, {
        time_zone: timeZone,
        start_at: dateTime.toISOString(),
        duration: appointmentType.duration,
        participants: [{ participant: provider.id }],
        // video_call,
      })
      .then((appointment) => {
        setAppointment(appointment)
        setCurrentScreen('success')
        return appointment
      })
  }, [source, appointmentType, timeZone, dateTime, provider])

  // Logic to remove provider from care team if it's the only appointment with this provider
  const removeProviderFromCareTeamIfNeeded = useCallback(
    async (appointment: Appointment) => {
      try {
        const member = appointment.member as Member
        const provider = appointment.participants[0].participant as User

        // Check if the patient has other appointments with this provider
        const patientAppointmentsWithThisProvider = await source.scheduling.appointments.list(
          {
            participant: [provider.id],
          }
        )

        // Remove provider from care team if it's the only appointment with this provider
        if (patientAppointmentsWithThisProvider.data.length <= 1) {
          const careTeamId = member.care_team as string
          const careTeam = await source.careTeams.retrieve(careTeamId)
          const careTeamParticipants = careTeam.participants as CareTeamParticipant[]

          // Remove the current provider from the care team
          const newCareTeamParticipants = careTeamParticipants.filter(
            (p) => p.user !== provider.id
          ) as CareTeamUpdateParamsParticipant[]

          logger.info(
            `removing provider ${provider.id} from care team - appointment ${appointment.id}, member ${member.id}`,
            'removeProviderFromCareTeamIfNeeded'
          )

          // Update the care team of the person removing the current provider
          await source.careTeams.update(careTeam.id, {
            participants: newCareTeamParticipants,
          })
        }
      } catch (error) {
        logger.error(
          'Error removing provider from care team',
          'removeProviderFromCareTeamIfNeeded'
        )
      }
    },
    [source]
  )

  const cancelAppointment = useCallback(
    async (cancellationReason: AppointmentCancellationReason) => {
      if (!appointment || !appointment.member) {
        setAlertText(
          'Please refresh the page and try again',
          'An Error Occurred'
        )
        return Promise.resolve()
      }

      // Remove provider from care team if needed
      await removeProviderFromCareTeamIfNeeded(appointment)

      return source.scheduling.appointments
        .cancel(appointment.id, { cancellation_reason: cancellationReason })
        .then(() => {
          clearAppointmentData()
          setAmplitudeBookingMetadata({
            source: BookingEventSource.APPOINTMENT_CANCEL_DONE,
            patient_action_id: null,
            patient_action_definition: null,
          })
        })
    },
    [source, appointment, removeProviderFromCareTeamIfNeeded]
  )

  // Set appointment and appointmentType when the previous
  // appointment is loaded
  useEffect(() => {
    if (
      previousAppointment &&
      !appointment &&
      currentScreen !== 'location' &&
      !rebookingFlowData
    ) {
      setAppointment(previousAppointment)
      setAppointmentType(
        previousAppointment.appointment_type as AppointmentType
      )
      setDateTime(new Date(previousAppointment.start_at))

      if (previousAppointment.participants.length) {
        setProvider(previousAppointment.participants[0].participant as User)
      }

      const member = previousAppointment.member as Member

      if (member.time_zone) {
        setTimeZone(member.time_zone)
      }
    }
  }, [previousAppointment, appointment])

  // Set appointmentType when loaded
  useEffect(() => {
    if (fetchedAppointmentType) {
      setAppointmentType(fetchedAppointmentType)
    }
  }, [fetchedAppointmentType])

  // Handle invalid appointment type
  useEffect(() => {
    if (appointmentTypeId && !isLoadingApptType && !fetchedAppointmentType) {
      history.push(RoutePaths.DASHBOARD)
    }
  }, [appointmentTypeId, fetchedAppointmentType, isLoadingApptType])

  // Handle invalid appointment
  useEffect(() => {
    if (appointmentId && !isLoadingAppt && !previousAppointment) {
      history.push(RoutePaths.DASHBOARD)
    }
  }, [appointmentId, previousAppointment, isLoadingAppt])

  // Handle polling if no zoom link yet
  useEffect(() => {
    let interval: NodeJS.Timeout

    if (appointment && !appointment.video_call) {
      let poll = 4
      interval = setInterval(() => {
        if (poll <= 0) {
          clearInterval(interval)
        } else {
          poll--
          source.scheduling.appointments
            .retrieve(appointment.id)
            .then((appt) => {
              if (appt.video_call) {
                setAppointment(appt)
                clearInterval(interval)
              }
            })
        }
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [appointment])

  return (
    <SchedulingContext.Provider
      value={{
        dateTime,
        timeZone,
        provider,
        isReschedule,
        amplitudeBookingMetadata,
        currentScreen,
        isLoadingApptType,
        appointmentType,
        appointment,
        selectedSlot,
        participantsChanged,
        rebookingFlowData,
        setAppointmentTypeId,
        setDateTime,
        setTimeZone,
        setProvider,
        nextScreen,
        previousScreen,
        bookAppointment,
        setCurrentScreen,
        setSelectedSlot,
        cancelAppointment,
        rescheduleAppointment,
        setRebookingFlowData,
      }}
    >
      {children}
    </SchedulingContext.Provider>
  )
}

export const useSchedulingContext = (): SchedulingContextInterface =>
  useContext(SchedulingContext)
